<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/notification_plans/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
  />
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {computed, inject, reactive, type Ref, unref,} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, {type Column} from '@utils/table/Table.vue';
import {enumOptionsProvider, useProvider} from '@utils/table/hooks/provider';
import type {Filter} from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {format} from 'date-fns';
import Export from '@utils/toolbar/panes/Export.vue';
import type {CampaignGroup, HydraCollection, NotificationPlan} from '@/entities/Entities';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => !props.modelValue);

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaigns.group': props.modelValue?.id ?? '',
  'inArray[campaigns.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => ({ ...fixedFilter.value, ...filter.value }));

const sortKey = useQueryRef<string | undefined>('sortKey', 'active');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const csvTitle = computed(() => t('csv.title.notificationsFromGroup', {
  group: props.modelValue?.name,
}));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.notificationList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'name',
    title: t('notificationPlan.name'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.name'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.notificationList.fieldGroups.general'),
    computed: (item: NotificationPlan) => item.campaigns?.map((campaign) => campaign.name).join(', '),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaigns',
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.notificationList.fieldGroups.general'),
    computed: (item: NotificationPlan) => item.campaigns?.map((campaign) => campaign.region?.name).join(', '),
    sortable: true,
    visible: true,
  },
  {
    key: 'active',
    csvTranslations: {
      true: t('notificationPlan.active.true'),
      false: t('notificationPlan.active.false'),
    },
    title: t('notificationPlan.active.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: boolean) => t(`notificationPlan.active.${item}`),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'bool',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.active.DEFAULT'),
        labelTrue: t('notificationPlan.active.true'),
        labelFalse: t('notificationPlan.active.false'),
      },
    },
  },
  {
    key: 'dateType',
    csvTranslations: {
      fix: t('notificationPlan.dateType.fix'),
      recurrent: t('notificationPlan.dateType.recurrent'),
      event: t('notificationPlan.dateType.event'),
      triggered: t('notificationPlan.dateType.triggered'),
    },
    title: t('notificationPlan.dateType.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`notificationPlan.dateType.${item}`),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/NotificationDateType', {}, (value) => ({
          value,
          label: t(`notificationPlan.dateType.${value}`),
        })),
        label: t('notificationPlan.dateType.DEFAULT'),
      },
    },
  },
  {
    key: 'lastExecution',
    title: t('notificationPlan.lastExecution'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.lastExecution'),
      },
    },
  },
  {
    key: 'nextExecution',
    title: t('notificationPlan.nextExecution'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.nextExecution'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<NotificationPlan>>('/notification_plans', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>
